// The Definitions tab of both Datasheet Composers: a searchable list of the labels on the
// templates, and the definition of the one selected.
//   scope 'master'  — the admin library's labels and their master text (Master Datasheet Composer).
//   scope 'project' — this database's labels; each shows this database's override when it has
//                     one, else the master text. Edits here write the override only.
// Reading needs the page's key; editing needs the global action.edithelp, as the help editor does.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Pencil, Trash2 } from 'lucide-react';
import { dsDefinitionsApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import StuiRichText, { StuiRichTextViewer } from '../../components/common/StuiRichText';
import { applySearch, SEARCH_HINT } from '../../utils/listSearch';
import { formatDateTime } from '../../utils/dateTime';
import { DEFINITION_FILTERS, filterDefinitions, definitionSummary, shownDefinition, isBlankHtml, definitionError } from './dsDefinitions';
import StuiSelect from '../../components/common/StuiSelect';

const SEARCH_FIELDS = ['label'];

const stamp = (def) => (def ? `${def.revUser || 'unknown'}, ${formatDateTime(def.revDate, { valueZone: 'utc' })}` : '');

export default function DefinitionsTab({ scope = 'master' }) {
  const { notify } = useNotifications();
  const { currentUser, hasPermission } = useAuth();
  const canEdit = hasPermission('action.edithelp', 'global');
  const api = dsDefinitionsApi[scope];
  const isProject = scope === 'project';

  const [rows, setRows] = useState([]);
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);   // label
  const [read, setRead] = useState(null);           // definition read for the selected label
  const [reading, setReading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState({ label: null, templates: [], loading: false });

  const loadLabels = useCallback(() => {
    setLoading(true);
    api.getLabels()
      .then(r => {
        const data = r.data ?? [];
        setRows(isProject ? data.labels ?? [] : data);
        if (isProject) setSlug(data.slug ?? '');
      })
      .catch(e => notify.error(definitionError(e, 'Failed to load the labels.')))
      .finally(() => setLoading(false));
  }, [api, isProject, notify]);

  useEffect(() => { loadLabels(); }, [loadLabels]);

  const loadDefinition = useCallback((label) => {
    setReading(true);
    api.get(label)
      .then(r => setRead(r.data))
      .catch(e => { setRead(null); notify.error(definitionError(e, 'Failed to load the definition.')); })
      .finally(() => setReading(false));
  }, [api, notify]);

  // Related datasheets: the templates carrying the label and the sheets using each.
  const loadUsage = useCallback((label) => {
    setUsage({ label, templates: [], loading: true });
    api.usage(label)
      .then(r => setUsage(u => (u.label === label ? { label, templates: r.data?.templates ?? [], loading: false } : u)))
      .catch(e => { setUsage({ label, templates: [], loading: false }); notify.error(definitionError(e, 'Failed to load the related datasheets.')); });
  }, [api, notify]);

  const visible = useMemo(
    () => applySearch(filterDefinitions(rows, filter), search, SEARCH_FIELDS),
    [rows, filter, search]);

  const pick = (label) => {
    const go = () => { setEditing(false); setSelected(label); setRead(null); loadDefinition(label); loadUsage(label); };
    if (editing) notify.confirm('Discard unsaved changes?', go);
    else go();
  };

  // What the detail pane shows and what an edit writes: the master text on the master page,
  // this database's override on the project page.
  const own = isProject ? read?.override : read?.master;
  const shown = isProject ? shownDefinition(read) : { kind: read?.master ? 'master' : 'none', def: read?.master };

  const startEdit = () => {
    // A new override starts from the master text, so a tweak doesn't mean retyping it.
    setDraft(own?.contentHtml || (isProject ? read?.master?.contentHtml : '') || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    if ((own?.contentHtml || '') !== draft && !isBlankHtml(draft)) notify.confirm('Discard unsaved changes?', () => setEditing(false));
    else setEditing(false);
  };

  const auditCfg = { headers: { 'X-Changed-By': currentUser?.email || '' } };

  const save = async () => {
    setSaving(true);
    try {
      await api.save(selected, isBlankHtml(draft) ? '' : draft, auditCfg);
      notify.success(isBlankHtml(draft) ? 'Definition removed' : 'Definition saved');
      setEditing(false);
      loadDefinition(selected);
      loadLabels();
    } catch (e) {
      notify.error(definitionError(e, 'Failed to save the definition.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = () => {
    const what = isProject ? `this database's definition of "${selected}"? The master definition will show instead.` : `the master definition of "${selected}"?`;
    notify.confirm(`Delete ${what}`, async () => {
      try {
        await api.remove(selected, auditCfg);
        notify.success('Definition deleted');
        loadDefinition(selected);
        loadLabels();
      } catch (e) {
        notify.error(definitionError(e, 'Failed to delete the definition.'));
      }
    });
  };

  const selectedRow = rows.find(r => r.label === selected);

  return (
    <div className="stui-list-view">
      <div className="stui-list-panel">
        <div className="stui-list-panel-header">
          <div className="stui-row-4 stui-w-full">
            <div className="stui-search-wrapper">
              <SearchClearIcon size={14} searchTerm={search} onClear={() => setSearch('')} />
              <input className="stui-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search labels..." title={SEARCH_HINT} />
            </div>
            <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={loadLabels} disabled={loading} title="Refresh"><RefreshCw size={14} /></button>
          </div>
        </div>
        <div className="stui-list-panel-header">
          <StuiSelect className="stui-select stui-w-full" value={filter} onChange={e => setFilter(e.target.value)} title="Show">
            {DEFINITION_FILTERS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </StuiSelect>
        </div>
        <div className="stui-list-scroll">
          {loading && rows.length === 0 ? (
            <div className="stui-list-empty">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="stui-list-empty">{rows.length === 0 ? 'No labels on any template.' : 'No labels match.'}</div>
          ) : visible.map(r => (
            <button key={r.label} className={`stui-list-item${r.label === selected ? ' selected' : ''}`} onClick={() => pick(r.label)}>
              <span className="stui-list-item-primary">{r.label}</span>
              <span className="stui-list-item-secondary">{definitionSummary(r, scope)}</span>
            </button>
          ))}
        </div>
        <div className="stui-list-add-row">
          <span className="stui-text-sm-muted">{visible.length} of {rows.length} labels</span>
        </div>
      </div>

      <div className="stui-detail-panel">
        {!selected ? (
          <div className="stui-detail-empty">Select a label to see its definition.</div>
        ) : (
          <>
            <div className="stui-detail-header">
              <h3>{selected}</h3>
              {canEdit && !editing && !reading && read && (
                <div className="stui-row-4">
                  <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={startEdit}
                    title={own ? 'Edit this definition' : isProject ? 'Write a definition for this database' : 'Write the master definition'}>
                    <Pencil size={14} /><span>{own ? 'Edit' : isProject ? 'Override' : 'Create'}</span>
                  </button>
                  {own && (
                    <button className="stui-btn stui-btn-danger-subtle stui-btn-sm" onClick={remove} title="Delete this definition">
                      <Trash2 size={14} /><span>Delete</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="stui-detail-content stui-mt-sm">
              <p className="stui-hint">
                {selectedRow ? definitionSummary(selectedRow, scope) : ''}
                {!isProject && read?.overriddenIn?.length > 0 && ` (${read.overriddenIn.join(', ')})`}
              </p>
              {reading ? (
                <p className="stui-label-note">Loading…</p>
              ) : editing ? (
                <>
                  {isProject && (
                    <p className="stui-hint">
                      This writes {slug ? `${slug}'s` : 'this database\'s'} own definition, which shows here instead of the master.
                    </p>
                  )}
                  <StuiRichText value={draft} onChange={setDraft} placeholder="What this label means on a datasheet..." minHeight={220} />
                  <div className="stui-row-8 stui-mt-sm">
                    <button className="stui-btn stui-btn-secondary" onClick={cancelEdit} disabled={saving}>Cancel</button>
                    <button className="stui-btn stui-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                  </div>
                </>
              ) : shown.kind === 'none' ? (
                <p className="stui-label-note">
                  No definition yet.{canEdit && ` Use ${isProject ? 'Override' : 'Create'} to write one.`}
                </p>
              ) : (
                <>
                  {isProject && (
                    <p className="stui-hint">
                      {shown.kind === 'override'
                        ? `${slug ? `${slug}'s` : 'This database\'s'} own definition, overriding the master.`
                        : 'Master definition (this database has no override).'}
                    </p>
                  )}
                  <StuiRichTextViewer html={shown.def.contentHtml} />
                  <p className="stui-hint stui-mt-sm">Last saved by {stamp(shown.def)}</p>
                </>
              )}
              <div className="stui-detail-section-header stui-mt-md">
                {`Related Datasheets${usage.loading ? '' : ` (${usage.templates.length})`}`}
              </div>
              {usage.loading ? (
                <p className="stui-label-note">Loading…</p>
              ) : usage.templates.length === 0 ? (
                <p className="stui-label-note">{isProject ? 'No datasheet in this database uses this label.' : 'No template in the library uses this label.'}</p>
              ) : (
                <div className="stui-data-grid-container stui-scroll-cap">
                  <table className="stui-data-grid stui-data-grid-auto">
                    <thead>
                      <tr>
                        <th>Datasheet</th><th>Kind</th><th>Rev</th>
                        {!isProject && <th>Published</th>}
                        <th>{isProject ? 'Index Sheets' : 'Catalog sheets'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage.templates.map(t => (
                        <tr key={t.id}>
                          <td className="stui-mono">{t.dsIndex}</td>
                          <td>{t.kind}</td>
                          <td>{t.rev}</td>
                          {!isProject && <td>{t.published ? 'Yes' : ''}</td>}
                          <td className={t.sheets.length ? 'stui-mono' : 'stui-td-muted'}>{t.sheets.length ? t.sheets.join(', ') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
